import { useEffect, useState, useCallback, useRef } from 'react';
import { useSession, useUser, useDescope, getSessionToken, Descope } from '@descope/react-sdk';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useAuthStore } from '../../stores/authStore';
import { useFilterStore } from '../../stores/filterStore';
import { fetchMe, getFilterOptions } from '../../api/dashboard';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * Descope session gate plus the dashboard scope gate.
 *
 * Signing in to the InternalApps project is not enough to get in: the backend requires a
 * `product:user` or `product:admin` scope on the JWT and answers 403 without one
 * (PEL-1881, see backend/auth.py). So a successful Descope login is followed by one
 * `/me` call, whose outcome decides which of three things renders — the app, the
 * "access denied" screen (403), or the sign-in flow again (401).
 */
export default function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isSessionLoading, sessionToken } = useSession();
  const { logout: descopeLogout } = useDescope();
  const { user } = useUser();
  const { setUser, setToken, logout: localLogout } = useAuthStore();
  const { setAvailableOptions } = useFilterStore();
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const hasLoggedOut = useRef(false);
  const hasCheckedAccess = useRef(false);

  // Listen for force-logout events from API client (e.g., on 401 responses)
  useEffect(() => {
    const handleForceLogout = () => {
      if (!hasLoggedOut.current) {
        hasLoggedOut.current = true;
        localLogout();
        descopeLogout();
        setHasAccess(null);
      }
    };

    window.addEventListener('auth:force-logout', handleForceLogout);
    return () => window.removeEventListener('auth:force-logout', handleForceLogout);
  }, [localLogout, descopeLogout]);

  useEffect(() => {
    if (isAuthenticated && user) {
      // Reset logout flag when user successfully authenticates
      hasLoggedOut.current = false;

      const token = sessionToken || getSessionToken();
      if (token) {
        setToken(token);
      } else {
        // If Descope says authenticated but we can't read a token, treat as unauthenticated.
        setToken(null);
        setUser(null);
        setHasAccess(null);
        return;
      }

      // Skip re-checking access if we've already verified this session
      // This prevents flickering on Descope token refreshes
      if (hasCheckedAccess.current && hasAccess !== null) {
        return;
      }

      setUser({
        name: user.name || user.email || 'Unknown',
        email: user.email || '',
        organization: undefined,
        role: 'user',
        isAdmin: false,
      });

      // Ask the backend whether this account holds a dashboard scope, and which one.
      setIsCheckingAccess(true);
      fetchMe()
        .then((me) => {
          hasCheckedAccess.current = true;
          setHasAccess(true);
          setUser({
            name: me.name || user.name || user.email || 'Unknown',
            email: me.email || user.email || '',
            organization: me.organization,
            role: me.role,
            isAdmin: me.isAdmin,
          });
          // Warm the filter options every tab reuses. A failure here is not an access
          // problem, so it must not flip the gate — just leave the options unset.
          return getFilterOptions()
            .then(setAvailableOptions)
            .catch(() => undefined);
        })
        .catch((error) => {
          if (error.response?.status === 401) {
            // Token invalid/expired (or wrong project) — force a real logout so SDK state resets.
            // Use ref to prevent infinite logout loop
            if (!hasLoggedOut.current) {
              hasLoggedOut.current = true;
              hasCheckedAccess.current = false;
              localLogout();
              descopeLogout();
              setHasAccess(null);
            }
            return;
          }
          if (error.response?.status === 403) {
            // Authenticated, but no `product:*` scope on the JWT.
            hasCheckedAccess.current = true;
            setHasAccess(false);
          }
        })
        .finally(() => {
          setIsCheckingAccess(false);
        });
    }
  }, [isAuthenticated, sessionToken, user, setUser, setToken, localLogout, descopeLogout, setAvailableOptions, hasAccess]);

  const handleLoginSuccess = useCallback(() => {
    // Session will be updated automatically by Descope SDK
    window.location.reload();
  }, []);

  const handleLoginError = useCallback((error: string) => {
    console.error('Login error:', error);
  }, []);

  if (isSessionLoading || isCheckingAccess) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
        <Typography variant="h6" sx={{ color: 'white' }}>
          Loading Product Dashboard...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 3,
        }}
      >
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
          Product Dashboard
        </Typography>
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 2,
            p: 3,
            minWidth: 360,
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          }}
        >
          <Descope
            flowId="sign-in"
            onSuccess={handleLoginSuccess}
            onError={(e) =>
              handleLoginError(e.detail.errorMessage ?? e.detail.errorDescription ?? 'Login failed')
            }
          />
        </Box>
      </Box>
    );
  }

  if (hasAccess === false) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          Your account isn't enabled for the Product Dashboard.
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
          Ask an admin to grant you the <code>product:user</code> scope in Descope.
        </Typography>
        <Button
          variant="outlined"
          sx={{
            borderColor: 'white',
            color: 'white',
            '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
          }}
          onClick={() => {
            localLogout();
            descopeLogout();
          }}
        >
          Sign Out
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
}
